import { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, Send } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { toast } from "sonner";
import { api_endpoint } from "@/constants/constant";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import { Checkbox } from "../../@/components/ui/checkbox";
import { Label } from "../../@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { ClipLoader } from "react-spinners";
import RecipientsSelectorDialog from "./recipientsSelectorDialog";
import { cn } from "@/lib/utils";

export default function MakeAnnouncementDialog({
  selectedCount = 0,
  onSubmit,
  courseTitle,
  courseId,
  mode = "course",
  cohortOptions = [],
  pathwayOptions = [],
  triggerLabel = "Make Announcement",
  triggerVariant = "outline",
  triggerClassName = "",
  showIcon = true,
}) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [deliveryModes, setDeliveryModes] = useState({
    inApp: true,
    email: false,
    slack: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [recipientsDialogOpen, setRecipientsDialogOpen] = useState(false);
  const [individualRecipientsDialogOpen, setIndividualRecipientsDialogOpen] = useState(false);
  const [recipientType, setRecipientType] = useState("cohort");
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [selectedPathways, setSelectedPathways] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [recipientsSearch, setRecipientsSearch] = useState("");
  const [showValidation, setShowValidation] = useState(false);

  const [groupRecipients, setGroupRecipients] = useState([]);
  const [groupRecipientsLoading, setGroupRecipientsLoading] = useState(false);
  const [showAllResolvedRecipients, setShowAllResolvedRecipients] = useState(false);


  const sanitizedContentName = (courseTitle || "general")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "announcement";

  const deliveryOptions = [
    { key: "inApp", label: "In-app Notification" },
    { key: "email", label: "Email #Important" },
    { key: "slack", label: "Slack/Discord" },
  ];

  const recipientsLabel =
    selectedCount > 0
      ? `${selectedCount} selected enrolment${selectedCount === 1 ? "" : "s"}`
      : "all enrolled students";

  const hasDeliverySelection = Object.values(deliveryModes).some(Boolean);

  const resetForm = () => {
    setSubject("");
    setMessage("");
    setDeliveryModes({ inApp: true, email: false, slack: false });
    setMarkdownEditorOpen(false);
    setRecipientsDialogOpen(false);
    setIndividualRecipientsDialogOpen(false);
    setRecipientType("cohort");
    setSelectedCohorts([]);
    setSelectedPathways([]);
    setSelectedRecipients([]);
    setRecipientsSearch("");
    setGroupRecipients([]);
    setGroupRecipientsLoading(false);
    setShowAllResolvedRecipients(false);
    setShowValidation(false);
  };

  const recipientLabel = useMemo(() => {
    if (mode === "group") {
      const parts = [];
      if (selectedRecipients.length) parts.push(`${selectedRecipients.length} recipient(s)`);
      if (selectedCohorts.length) parts.push(`${selectedCohorts.length} cohort(s)`);
      if (selectedPathways.length) parts.push(`${selectedPathways.length} pathway(s)`);
      return parts.length ? parts.join(" and ") : "choose recipients";
    }
    return courseTitle ? `${courseTitle} students` : "course students";
  }, [mode, selectedRecipients.length, selectedCohorts.length, selectedPathways.length, courseTitle])

  const hasRecipientsSelected =
    selectedRecipients.length > 0 || selectedCohorts.length > 0 || selectedPathways.length > 0;

  const subjectError = showValidation && !subject.trim();
  const messageError = showValidation && !message.trim();
  const recipientsError = mode === "group" && showValidation && !hasRecipientsSelected;

  const recipientsSummary = useMemo(() => {
    const total = new Set([
      ...(selectedRecipients || []).map((id) => String(id)),
      ...(groupRecipients || []).map((u) => String(u.id)),
    ]).size;

    if (total > 0) return `${total} recipient${total === 1 ? "" : "s"} selected`;
    return "Select recipients";
  }, [selectedRecipients, groupRecipients]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!open || mode !== "group") return;
      if (users.length > 0) return;
      if (!api_endpoint) return;

      try {
        setUsersLoading(true);
        const token = await getToken();
        if (!token) return;
        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const allUsers = await fetchAllPaginatedItems({
          url: `${api_endpoint}/user/users`,
          token,
          selectItems: (data) => data?.users ?? [],
          mode: "skip",
          pageSize: 200,
          headers: reqConfig.headers,
        });

        const deduped = new Map();
        for (const item of allUsers || []) {
          const id = item?.id ?? item?.user_id ?? item?._id;
          if (id == null) continue;
          const key = String(id);
          const existing = deduped.get(key);
          if (!existing) {
            deduped.set(key, item);
            continue;
          }
          deduped.set(key, { ...existing, ...item });
        }

        setUsers(Array.from(deduped.values()));
      } catch (error) {
        console.error("Failed to fetch users", error);
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [open, mode, getToken, users.length]);

  useEffect(() => {
    const fetchGroupRecipients = async () => {
      if (!open || mode !== "group") return;
      if (!api_endpoint) return;

      const cohortIds = (selectedCohorts || []).map(String);
      const pathwayIds = (selectedPathways || []).map(String);
      if (cohortIds.length === 0 && pathwayIds.length === 0) {
        setGroupRecipients([]);
        return;
      }

      try {
        setGroupRecipientsLoading(true);
        const token = await getToken();
        if (!token) return;

        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        const cohortRequests = cohortIds.map((id) =>
          fetchAllPaginatedItems({
            url: `${api_endpoint}/cohort/enrolments/${id}`,
            token,
            headers: reqConfig.headers,
            mode: "skip",
            pageSize: 200,
            maxPages: 200,
            dedupe: false,
            selectItems: (data) => data?.enrolments ?? [],
          })
        );

        const pathwayRequests = pathwayIds.map((id) =>
          axios.get(`${api_endpoint}/pathway/enrollments/${id}`, reqConfig)
        );

        const [cohortResults, pathwayResults] = await Promise.all([
          Promise.allSettled(cohortRequests),
          Promise.allSettled(pathwayRequests),
        ]);

        const resolved = [];

        for (const r of cohortResults) {
          if (r.status !== "fulfilled") continue;
          const enrolments = Array.isArray(r.value) ? r.value : [];
          for (const item of enrolments) {
            const id = item.user_id ?? item.id;
            if (id == null) continue;
            const first = item.user_first_name ?? item.first_name ?? "";
            const last = item.user_last_name ?? item.last_name ?? "";
            const name = `${first} ${last}`.trim();
            resolved.push({
              id: String(id),
              name: name || "Not available",
              email: item.user_email ?? item.email ?? "",
              source: "cohort",
            });
          }
        }

        for (const r of pathwayResults) {
          if (r.status !== "fulfilled") continue;
          const enrollments = r.value?.data?.enrollments || [];
          for (const item of enrollments) {
            const id = item.user_id ?? item.id;
            if (id == null) continue;
            const first = item.first_name ?? item.user_first_name ?? "";
            const last = item.last_name ?? item.user_last_name ?? "";
            const name = `${first} ${last}`.trim();
            resolved.push({
              id: String(id),
              name: name || "Not available",
              email: item.email ?? item.user_email ?? "",
              source: "pathway",
            });
          }
        }

        const dedupedMap = new Map();
        for (const u of resolved) {
          const key = String(u.id);
          const existing = dedupedMap.get(key);
          if (!existing) {
            dedupedMap.set(key, u);
            continue;
          }

          const existingName = (existing.name || "").trim();
          const nextName = (u.name || "").trim();
          if (existingName === "Not available" && nextName && nextName !== "Not available") {
            dedupedMap.set(key, { ...existing, ...u });
          }
        }

        const deduped = Array.from(dedupedMap.values()).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
        setGroupRecipients(deduped);
      } catch (error) {
        console.error("Failed to resolve cohort/pathway recipients", error);
        setGroupRecipients([]);
      } finally {
        setGroupRecipientsLoading(false);
      }
    };

    fetchGroupRecipients();
  }, [open, mode, selectedCohorts, selectedPathways, getToken]);

  const filteredUsers = useMemo(() => {
    const term = recipientsSearch.toLowerCase().trim();
    if (!term) return users;
    return (users || []).filter((u) => {
      const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.toLowerCase();
      const email = (u.email ?? "").toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, recipientsSearch]);

  const selectedRecipientDetails = useMemo(() => {
    if (!selectedRecipients.length) return [];
    return selectedRecipients
      .map((id) => users.find((u) => String(u.id) === String(id)))
      .filter(Boolean);
  }, [selectedRecipients, users]);

  const resolvedRecipientsForDisplay = useMemo(() => {
    const selectedIds = new Set((selectedRecipients || []).map((id) => String(id)));
    return (groupRecipients || []).filter((u) => !selectedIds.has(String(u.id)));
  }, [groupRecipients, selectedRecipients]);

  const resolvedRecipientsCollapsed = useMemo(() => {
    if (showAllResolvedRecipients) return resolvedRecipientsForDisplay;
    return resolvedRecipientsForDisplay.slice(0, 20);
  }, [resolvedRecipientsForDisplay, showAllResolvedRecipients]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowValidation(true);
    if (submitting) return;
    if (!hasDeliverySelection) return;
    if (!subject.trim() || !message.trim()) return;
    if (mode === "group" && !hasRecipientsSelected) return;

    const payload = {
      title: subject.trim(),
      message: message.trim(),
      email_required: deliveryModes.email,
      in_app: deliveryModes.inApp,
      slack_discord: deliveryModes.slack,
    };

    if (mode === "course" && courseId) {
      payload.course_id = courseId;
    } else if (mode === "group") {
      if (selectedRecipients.length) payload.user_ids = selectedRecipients;
      if (selectedCohorts.length) payload.cohort_ids = selectedCohorts;
      if (selectedPathways.length) payload.pathway_ids = selectedPathways;
    }

    try {
      setSubmitting(true);
      if (!api_endpoint) {
        throw new Error("Missing API endpoint configuration");
      }
      const token = await getToken();
      if (!token) {
        throw new Error("No auth token available");
      }

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.post(`${api_endpoint}/notification/create-announcement`, payload, reqConfig);
      toast.success("Announcement sent successfully");
      onSubmit?.(payload);
      resetForm();
      setOpen(false);
    } catch (error) {
      console.error("Failed to send announcement", error);
      const description =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to send announcement. Please try again.";
      toast.error(description);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMode = (mode) => {
    setDeliveryModes((prev) => ({
      ...prev,
      [mode]: !prev[mode],
    }));
  };

  const handleMarkdownSave = (content) => {
    setMessage(content || "");
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleRecipientTypeChange = (value) => {
    if (!value) return;
    setRecipientType(value);
  };

  return (
    <>
      <Button
        type="button"
        variant={triggerVariant}
        className={`w-full sm:w-auto flex items-center justify-center gap-2 ${triggerClassName}`}
        onClick={() => setOpen(true)}
      >
        {triggerLabel}
        {showIcon && <Send className="w-4 h-4" />}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) {
            resetForm();
          }
        }}
      >
        <DialogContent className="h-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">Make an Announcement</DialogTitle>
            <DialogDescription className="text-sm text-(--neutral-700)">
              Send an announcement to {recipientLabel}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="announcement-subject">Subject *</Label>
              <Input
                id="announcement-subject"
                placeholder="NEW Topics added"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                className={cn(
                  subjectError &&
                    "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                )}
              />
              {subjectError && (
                <p className="text-sm text-(--color-destructive)">This is required</p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="announcement-message">Message *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size=""
                  onClick={() => setMarkdownEditorOpen(true)}
                >
                  Open Markdown Editor
                </Button>
              </div>
              <Textarea
                id="announcement-message"
                rows={5}
                placeholder="Enter your message here. Supports markdown"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                className={cn(
                  messageError &&
                    "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                )}
              />
              {messageError && (
                <p className="text-sm text-(--color-destructive)">This is required</p>
              )}
            </div>

            {mode === "group" && (
              <div className="space-y-2">
                <Label className="font-heading">Recipients*</Label>
                <div className="flex w-full items-stretch gap-0.5">
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "flex-1 w-full min-w-0 rounded-l-md rounded-r-none",
                      recipientsError && "border-(--color-destructive)"
                    )}
                    onClick={() => setIndividualRecipientsDialogOpen(true)}
                  >
                    {recipientsSummary}
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-none w-12 rounded-l-none rounded-r-md px-0 flex items-center justify-center"
                        aria-label="More recipient actions"
                      >
                        <ChevronDownIcon className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-55">
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                          setRecipientsDialogOpen(true);
                        }}
                      >
                        Auto-populate by cohort/pathway
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex flex-wrap gap-2">
                  {selectedRecipientDetails.map((u) => (
                    <span key={`recipient-chip-${u.id}`} className="tag-chip">
                      {`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email || u.id}
                      <button
                        type="button"
                        className="chip-remove"
                        onClick={() =>
                          setSelectedRecipients((prev) =>
                            prev.filter((value) => String(value) !== String(u.id))
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}

                  {groupRecipientsLoading ? (
                    <span className="text-xs text-(--neutral-600) font-body">
                      Resolving cohort/pathway recipients…
                    </span>
                  ) : resolvedRecipientsCollapsed.length > 0 ? (
                    resolvedRecipientsCollapsed.map((u) => (
                      <span
                        key={`resolved-recipient-${u.id}`}
                        className="tag-chip opacity-90"
                        title={u.email || u.name}
                      >
                        {u.name || u.email || u.id}
                      </span>
                    ))
                  ) : null}

                  {!groupRecipientsLoading &&
                    resolvedRecipientsForDisplay.length > 20 && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => setShowAllResolvedRecipients((prev) => !prev)}
                      >
                        {showAllResolvedRecipients
                          ? "Show less"
                          : `Show all (${resolvedRecipientsForDisplay.length})`}
                      </Button>
                    )}

                  {!hasRecipientsSelected && (
                    <p
                      className={cn(
                        "text-sm",
                        recipientsError
                          ? "text-(--color-destructive)"
                          : "text-(--color-destructive)"
                      )}
                    >
                      {recipientsError
                        ? "This is required"
                        : "Select recipients to enable sending announcements."}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Label>Delivery Modes</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {deliveryOptions.map((option) => (
                  <label
                    key={option.key}
                    className={`flex items-center gap-2 rounded-md border border-(--neutral-300) px-3 py-2 text-sm font-body cursor-pointer transition text-(--neutral-900) ${
                      deliveryModes[option.key]
                        ? "border-(--color-primary) bg-(--neutral-50)"
                        : "border-(--neutral-300)"
                    }`}
                  >
                    <Checkbox
                      checked={deliveryModes[option.key]}
                      onCheckedChange={() => toggleMode(option.key)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter className="">
              <Button
                type="button"
                variant="outline"
                className=""
                onClick={() => {
                  resetForm();
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className=""
                disabled={ !hasDeliverySelection || submitting ||  (mode === "group" && !hasRecipientsSelected)}
              >
                {submitting ? <ClipLoader size={20} color="#fff"/> : "Send Announcement"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MarkdownEditorDialog
        open={markdownEditorOpen}
        initialValue={message}
        onClose={handleMarkdownClose}
        onSave={handleMarkdownSave}
        contentFolder="announcements"
        contentName={sanitizedContentName}
      />

      <RecipientsSelectorDialog
        open={recipientsDialogOpen}
        onOpenChange={setRecipientsDialogOpen}
        cohortOptions={cohortOptions}
        pathwayOptions={pathwayOptions}
        recipientType={recipientType}
        onRecipientTypeChange={handleRecipientTypeChange}
        selectedCohorts={selectedCohorts}
        setSelectedCohorts={setSelectedCohorts}
        selectedPathways={selectedPathways}
        setSelectedPathways={setSelectedPathways}
      />

      <Dialog open={individualRecipientsDialogOpen} onOpenChange={setIndividualRecipientsDialogOpen}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-(--neutral-900)">Select Recipients</DialogTitle>
            <DialogDescription className="text-sm text-(--neutral-600) font-body">
              Search and select individual users to receive the announcement.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 flex flex-col gap-4">
            <div className="space-y-2">
              <Input
                placeholder={usersLoading ? "Loading users..." : "Search recipients"}
                value={recipientsSearch}
                onChange={(e) => setRecipientsSearch(e.target.value)}
                className="w-full font-body"
                disabled={usersLoading}
              />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
              {usersLoading ? (
                <div className="text-sm text-(--neutral-600)">Loading recipients…</div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-sm text-(--neutral-600)">
                  {recipientsSearch.trim()
                    ? `No users found for "${recipientsSearch.trim()}".`
                    : "No users available."}
                </div>
              ) : (
                filteredUsers.map((u, index) => (
                  <div
                    key={`${String(u.id ?? "user")}-${index}`}
                    className="flex items-center gap-2 border-b border-(--neutral-300) py-2"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRecipients.some((id) => String(id) === String(u.id))}
                      onChange={() => {
                        setSelectedRecipients((prev) => {
                          const exists = prev.some((id) => String(id) === String(u.id));
                          return exists
                            ? prev.filter((id) => String(id) !== String(u.id))
                            : [...prev, String(u.id)];
                        });
                      }}
                      className="w-4 h-4"
                    />
                    <span className="font-body">
                      {u.first_name} {u.last_name} ({u.email})
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter className="mt-auto pt-4">
            <Button variant="outline" type="button" onClick={() => setIndividualRecipientsDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => setIndividualRecipientsDialogOpen(false)} disabled={selectedRecipients.length === 0}>
              Add Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
